import type { RouteResource } from 'react-resource-router';

import { eocUserResource, type UserResourceType } from '@atlassian/eoc-resources';
import type { ClusterPeriod } from '@atlassian/eoc-schedule-common';

import type { OpsTeam, ResourceDependencies } from '../../common/types';
import type { MyTeamsResponse } from '../endpoints/my-teams/types';

import { createMyTeamsResource } from './my-teams';
import { createOpsTeamsResource } from './ops-teams';
import { createSchedulesResource } from './schedules';
import type { SchedulesResponse } from './schedules/endpoints/types';
import { createStatusResource } from './status';
import { createUserTimelineResource } from './user-timeline';

export const myTeamsResource: RouteResource<MyTeamsResponse | null> = createMyTeamsResource();
export const opsTeamsResource: RouteResource<OpsTeam[] | null> = createOpsTeamsResource();
export const statusResource: RouteResource<{
	currentDate: number;
	currentShiftEnd: number | null;
	currentShiftEndOffset: number;
	isOnCall: boolean;
	isParticipantOfAnyRotation: boolean;
	nextEndDate: number | null;
	nextEndOffset: number;
	nextStartDate: number | null;
	nextStartOffset: number;
} | null> = createStatusResource();
export const userTimelineResource: RouteResource<{
	data: {
		id: string;
		periods: ClusterPeriod[];
		timeZone: string;
		totalScheduleCount: number;
		totalTeamCount: number;
	};
	endEpoch: number;
	endOffset: number;
	nowEpoch: number;
	nowOffset: number;
	startEpoch: number;
	startOffset: number;
} | null> = createUserTimelineResource();
export const schedulesResource: RouteResource<SchedulesResponse | null> = createSchedulesResource();

export const resources: (
	| RouteResource<UserResourceType>
	| RouteResource<MyTeamsResponse | null>
	| RouteResource<OpsTeam[] | null>
	| RouteResource<{
			currentDate: number;
			currentShiftEnd: number | null;
			currentShiftEndOffset: number;
			isOnCall: boolean;
			isParticipantOfAnyRotation: boolean;
			nextEndDate: number | null;
			nextEndOffset: number;
			nextStartDate: number | null;
			nextStartOffset: number;
	  } | null>
)[] = [eocUserResource, createMyTeamsResource(), createStatusResource(), createOpsTeamsResource()];

export const widgetResources: (
	| (() => RouteResource<UserResourceType>)
	| ((
			callback?: (dependencies: ResourceDependencies) => Promise<boolean>,
			depends?: string[],
	  ) => RouteResource<MyTeamsResponse | null>)
	| ((
			callback?: (dependencies: ResourceDependencies) => Promise<boolean>,
			depends?: string[],
	  ) => RouteResource<{
			currentDate: number;
			currentShiftEnd: number | null;
			currentShiftEndOffset: number;
			isOnCall: boolean;
			isParticipantOfAnyRotation: boolean;
			nextEndDate: number | null;
			nextEndOffset: number;
			nextStartDate: number | null;
			nextStartOffset: number;
	  } | null>)
	| ((
			callback?: (dependencies: ResourceDependencies) => Promise<boolean>,
			depends?: string[],
	  ) => RouteResource<{
			data: {
				id: string;
				periods: ClusterPeriod[];
				timeZone: string;
				totalScheduleCount: number;
				totalTeamCount: number;
			};
			endEpoch: number;
			endOffset: number;
			nowEpoch: number;
			nowOffset: number;
			startEpoch: number;
			startOffset: number;
	  } | null>)
	| ((
			callback?: (dependencies: ResourceDependencies) => Promise<boolean>,
			depends?: string[],
	  ) => RouteResource<SchedulesResponse | null>)
)[] = [
	(): RouteResource<UserResourceType> => eocUserResource,
	(
		callback?: (dependencies: ResourceDependencies) => Promise<boolean>,
		depends?: string[],
	): RouteResource<MyTeamsResponse | null> => createMyTeamsResource(callback, depends),
	(
		callback?: (dependencies: ResourceDependencies) => Promise<boolean>,
		depends?: string[],
	): RouteResource<{
		currentDate: number;
		currentShiftEnd: number | null;
		currentShiftEndOffset: number;
		isOnCall: boolean;
		isParticipantOfAnyRotation: boolean;
		nextEndDate: number | null;
		nextEndOffset: number;
		nextStartDate: number | null;
		nextStartOffset: number;
	} | null> => createStatusResource(callback, depends),
	(
		callback?: (dependencies: ResourceDependencies) => Promise<boolean>,
		depends?: string[],
	): RouteResource<{
		data: {
			id: string;
			periods: ClusterPeriod[];
			timeZone: string;
			totalScheduleCount: number;
			totalTeamCount: number;
		};
		endEpoch: number;
		endOffset: number;
		nowEpoch: number;
		nowOffset: number;
		startEpoch: number;
		startOffset: number;
	} | null> => createUserTimelineResource(callback, depends),
	(
		callback?: (dependencies: ResourceDependencies) => Promise<boolean>,
		depends?: string[],
	): RouteResource<SchedulesResponse | null> => createSchedulesResource(callback, depends),
];

export {
	createMyTeamsResource,
	createStatusResource,
	createUserTimelineResource,
	createSchedulesResource,
	createOpsTeamsResource,
};
